import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  CheckCircle as CompleteIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import billingService, { 
  BillingPeriod, 
  BillingPeriodStatus, 
  CreateBillingPeriodRequest,
  UpdateBillingPeriodRequest 
} from '../../services/newBillingService';
import PageLayout from '@/components/Layout/PageLayout';

const BillingPeriodsPage: React.FC = () => {
  const [billingPeriods, setBillingPeriods] = useState<BillingPeriod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<BillingPeriod | null>(null);
  const [formData, setFormData] = useState<CreateBillingPeriodRequest>({
    name: '',
    description: '',
    startDate: '',
    endDate: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadBillingPeriods();
  }, []);

  const loadBillingPeriods = async () => {
    try {
      setLoading(true);
      const response = await billingService.billingPeriod.getBillingPeriods();
      if (response.success && response.data) {
        // Backend returns paginated response
        setBillingPeriods(response.data.billingPeriods || []);
      } else {
        setError('Failed to load billing periods');
        setBillingPeriods([]);
      }
    } catch (err: any) {
      setError(err.message);
      setBillingPeriods([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingPeriod(null);
    setFormData({
      name: '',
      description: '',
      startDate: '',
      endDate: ''
    });
    setOpenDialog(true);
  };

  const handleEdit = (period: BillingPeriod) => {
    setEditingPeriod(period);
    setFormData({
      name: period.name,
      description: period.description || '',
      startDate: period.startDate.split('T')[0],
      endDate: period.endDate.split('T')[0]
    });
    setOpenDialog(true);
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      if (editingPeriod) {
        // Update existing period
        const updateRequest: UpdateBillingPeriodRequest = {
          name: formData.name,
          description: formData.description,
          startDate: formData.startDate,
          endDate: formData.endDate
        };
        await billingService.billingPeriod.updateBillingPeriod(editingPeriod.id, updateRequest);
      } else {
        // Create new period
        await billingService.billingPeriod.createBillingPeriod(formData);
      }

      setOpenDialog(false);
      await loadBillingPeriods();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePeriod = async (periodId: number) => {
    try {
      setSubmitting(true);
      await billingService.billingPeriod.closeBillingPeriod(periodId);
      await loadBillingPeriods();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCompletePeriod = async (periodId: number) => {
    try {
      setSubmitting(true);
      await billingService.billingPeriod.completeBillingPeriod(periodId);
      await loadBillingPeriods();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: BillingPeriodStatus): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case BillingPeriodStatus.Active: return 'success';
      case BillingPeriodStatus.Closed: return 'warning';
      case BillingPeriodStatus.Billed: return 'info';
      case BillingPeriodStatus.Completed: return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status: BillingPeriodStatus): string => {
    switch (status) {
      case BillingPeriodStatus.Active: return 'Active';
      case BillingPeriodStatus.Closed: return 'Closed';
      case BillingPeriodStatus.Billed: return 'Billed';
      case BillingPeriodStatus.Completed: return 'Completed';
      default: return 'Unknown';
    }
  };

  const canEdit = (period: BillingPeriod): boolean => {
    return period.status === BillingPeriodStatus.Active;
  };

  const canClose = (period: BillingPeriod): boolean => {
    return period.status === BillingPeriodStatus.Active;
  };

  const canComplete = (period: BillingPeriod): boolean => {
    return period.status === BillingPeriodStatus.Billed;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PageLayout>
        <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Billing Periods
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          disabled={submitting}
        >
          Create New Period
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Bills Generated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingPeriods.map((period) => (
                  <TableRow key={period.id}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2">{period.name}</Typography>
                        {period.description && (
                          <Typography variant="body2" color="text.secondary">
                            {period.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(period.startDate), 'MMM dd, yyyy')} - {format(new Date(period.endDate), 'MMM dd, yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(period.status)}
                        color={getStatusColor(period.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {format(new Date(period.createdAt), 'MMM dd, yyyy HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {period.billsGeneratedAt ? (
                        <Typography variant="body2">
                          {format(new Date(period.billsGeneratedAt), 'MMM dd, yyyy HH:mm')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Not generated
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            href={`/billing/periods/${period.id}`}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        
                        {canEdit(period) && (
                          <Tooltip title="Edit Period">
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(period)}
                              disabled={submitting}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {canClose(period) && (
                          <Tooltip title="Close Period">
                            <IconButton
                              size="small"
                              onClick={() => handleClosePeriod(period.id)}
                              disabled={submitting}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {canComplete(period) && (
                          <Tooltip title="Complete Period">
                            <IconButton
                              size="small"
                              onClick={() => handleCompletePeriod(period.id)}
                              disabled={submitting}
                            >
                              <CompleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {billingPeriods.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No billing periods found. Create your first billing period to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPeriod ? 'Edit Billing Period' : 'Create New Billing Period'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Period Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., September 2024"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              placeholder="Optional description for this billing period"
            />
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                fullWidth
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                required
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !formData.name || !formData.startDate || !formData.endDate}
          >
            {submitting ? <CircularProgress size={20} /> : (editingPeriod ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageLayout>
  );
};

export default BillingPeriodsPage;